<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import {
    RENDER_TYPES,
    LIGHTINGS,
    VIEW_ANGLES,
    COLOR_TONES,
    MATERIALS,
    ASPECTS,
    SIZES,
    ROOM_SOURCES,
    WALL_MODES,
    FLOOR_MODES,
    CEILING_MODES,
    BACKSPLASH_MODES,
    ISLAND_MODES,
    ACCESSORY_LEVELS,
    ARCH_SCENES,
    ARCH_SURROUNDINGS,
    ARCH_ACTIVITY,
    ARCH_GROUNDS,
    SKY_MODES,
    LAND_SCENES,
    LAND_TERRAINS,
    LAND_WATER,
    LAND_HERO_TREES,
    LAND_GROUNDS,
    environmentsFor,
    parseSize,
    aiOptLabelKey,
    formatAiModelLabel,
    type Aspect,
    type Lighting,
    type ColorTone,
    type Material,
    type AutoEnvironment,
    type AutoArchitecture,
    type AutoLandscape,
    type AiModelDto,
    type RenderDto,
    type RenderParams,
  } from '@wood-viewer/shared';
  import {
    ApiError,
    getAiModels,
    createRender,
    getRender,
    saveRender,
    deleteRender,
    uploadThumbnail,
  } from '../lib/api/client';
  import { t } from '../i18n/index.svelte';
  import Icon from './Icon.svelte';

  let {
    token,
    authKey,
    capture,
    captureGuide,
    onClose,
    onToast,
    onSaved,
  }: {
    token: string;
    authKey: string;
    /** chụp khung cảnh hiện tại đúng W×H (SceneManager.captureHighRes). */
    capture: (width: number, height: number) => Promise<Blob>;
    /** 04a: chụp pass cấu trúc (line-pass) đúng W×H, cùng camera (SceneManager.captureGuide). */
    captureGuide: (width: number, height: number) => Promise<Blob>;
    onClose: () => void;
    onToast: (msg: string) => void;
    /** gọi khi vừa LƯU 1 ảnh → viewer làm mới gallery công khai. */
    onSaved?: () => void;
  } = $props();

  // ----- nhớ tham số lần render gần nhất (04b §4) -----
  const STORAGE_KEY = 'wv_airender_params';
  /** Trong panel, 3 field v3 (10e) luôn được khởi tạo đầy đủ (RenderParams để chúng optional). */
  type PanelParams = RenderParams & {
    lockGeometry: boolean; // 04a: toggle chủ "Giữ nguyên mô hình 3D" — panel luôn có giá trị
    preserveFurnitureStrength: number;
    contextCreativity: number;
    autoEnvironment: AutoEnvironment;
    autoArchitecture: AutoArchitecture; // 10f/04g — luôn đủ giá trị trong panel
    autoLandscape: AutoLandscape;
  };
  const DEFAULTS: PanelParams = {
    lockGeometry: true, // 04a: mặc định BẬT (ràng buộc cứng + gửi ảnh guide)
    renderType: 'interior',
    lighting: [],
    lightingText: '',
    viewAngle: 'keep',
    environment: 'living-room',
    environmentText: '',
    colorTone: [],
    materials: [],
    description: '',
    negativePrompt: '',
    aiModel: '',
    size: '1920x1080',
    aspect: '16:9',
    creativity: 65,
    // v3 (10e): 2 thang sáng tạo + tự tạo không gian phòng
    preserveFurnitureStrength: 90,
    contextCreativity: 65,
    autoEnvironment: {
      roomSource: 'ai-generated',
      wall: 'auto',
      floor: 'auto',
      ceiling: 'auto',
      backsplash: 'auto',
      island: 'auto',
      accessory: 'low',
    },
    // v6 (10f/04g): điều khiển scene ngoại cảnh — mọi field 'auto' = AI tự quyết
    autoArchitecture: {
      sceneType: 'auto',
      surroundingBuildings: 'auto',
      activity: 'auto',
      ground: 'auto',
      sky: 'auto',
    },
    autoLandscape: {
      sceneType: 'auto',
      terrain: 'auto',
      water: 'auto',
      heroTree: 'auto',
      ground: 'auto',
      sky: 'auto',
    },
  };

  /** ASPECT gần nhất theo w/h của size (để backend lái provider — backend zero-change). */
  function aspectFromSize(size: string): Aspect {
    const { w, h } = parseSize(size);
    const r = w / h;
    let best: Aspect = ASPECTS[0];
    let bestDiff = Infinity;
    for (const a of ASPECTS) {
      const [aw, ah] = a.split(':').map(Number);
      const diff = Math.abs(r - aw / ah);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = a;
      }
    }
    return best;
  }

  /** chọn value đơn nếu hợp lệ theo enum hiện hành, ngược lại fallback default. */
  function pick<T>(set: readonly T[], v: unknown, def: T): T {
    return set.includes(v as T) ? (v as T) : def;
  }
  /** lọc mảng đã lưu theo enum hiện hành (bỏ value lạ → không vỡ khi enum đổi). */
  function pickArr<T>(set: readonly T[], v: unknown): T[] {
    return Array.isArray(v) ? (v.filter((x) => set.includes(x as T)) as T[]) : [];
  }

  /** nạp + làm sạch tham số đã lưu; thiếu/parse lỗi/localStorage không khả dụng → default. */
  /** số 0..100 hợp lệ, ngược lại default. */
  function pickNum(v: unknown, def: number): number {
    return typeof v === 'number' && v >= 0 && v <= 100 ? v : def;
  }

  function loadSaved(): PanelParams {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return structuredClone(DEFAULTS);
      const s = JSON.parse(raw) as Partial<PanelParams>;
      const size = pick(SIZES, s.size, '1920x1080');
      const renderType = pick(RENDER_TYPES, s.renderType, DEFAULTS.renderType);
      const envSet = environmentsFor(renderType); // environment hợp lệ theo loại (10e)
      const ae = (s.autoEnvironment ?? {}) as Partial<AutoEnvironment>;
      const d = DEFAULTS.autoEnvironment;
      const aa = (s.autoArchitecture ?? {}) as Partial<AutoArchitecture>;
      const da = DEFAULTS.autoArchitecture;
      const al = (s.autoLandscape ?? {}) as Partial<AutoLandscape>;
      const dl = DEFAULTS.autoLandscape;
      return {
        lockGeometry: typeof s.lockGeometry === 'boolean' ? s.lockGeometry : true,
        renderType,
        lighting: pickArr(LIGHTINGS, s.lighting),
        lightingText: typeof s.lightingText === 'string' ? s.lightingText : '',
        viewAngle: pick(VIEW_ANGLES, s.viewAngle, DEFAULTS.viewAngle),
        environment: pick(envSet, s.environment, envSet[0]),
        environmentText: typeof s.environmentText === 'string' ? s.environmentText : '',
        colorTone: pickArr(COLOR_TONES, s.colorTone),
        materials: pickArr(MATERIALS, s.materials),
        description: typeof s.description === 'string' ? s.description : '',
        negativePrompt: typeof s.negativePrompt === 'string' ? s.negativePrompt : '',
        aiModel: typeof s.aiModel === 'string' ? s.aiModel : '',
        size,
        aspect: aspectFromSize(size), // dẫn xuất từ size (không tin aspect đã lưu)
        creativity: pickNum(s.creativity, DEFAULTS.creativity),
        // v3 (10e): 2 thang + tự tạo không gian phòng (làm sạch theo enum tương ứng)
        preserveFurnitureStrength: pickNum(
          s.preserveFurnitureStrength,
          DEFAULTS.preserveFurnitureStrength,
        ),
        contextCreativity: pickNum(s.contextCreativity, DEFAULTS.contextCreativity),
        autoEnvironment: {
          roomSource: pick(ROOM_SOURCES, ae.roomSource, d.roomSource),
          wall: pick(WALL_MODES, ae.wall, d.wall),
          floor: pick(FLOOR_MODES, ae.floor, d.floor),
          ceiling: pick(CEILING_MODES, ae.ceiling, d.ceiling),
          backsplash: pick(BACKSPLASH_MODES, ae.backsplash, d.backsplash),
          island: pick(ISLAND_MODES, ae.island, d.island),
          accessory: pick(ACCESSORY_LEVELS, ae.accessory, d.accessory),
        },
        // v6 (10f/04g): scene ngoại cảnh (lạ/thiếu → 'auto')
        autoArchitecture: {
          sceneType: pick(ARCH_SCENES, aa.sceneType, da.sceneType),
          surroundingBuildings: pick(ARCH_SURROUNDINGS, aa.surroundingBuildings, da.surroundingBuildings),
          activity: pick(ARCH_ACTIVITY, aa.activity, da.activity),
          ground: pick(ARCH_GROUNDS, aa.ground, da.ground),
          sky: pick(SKY_MODES, aa.sky, da.sky),
        },
        autoLandscape: {
          sceneType: pick(LAND_SCENES, al.sceneType, dl.sceneType),
          terrain: pick(LAND_TERRAINS, al.terrain, dl.terrain),
          water: pick(LAND_WATER, al.water, dl.water),
          heroTree: pick(LAND_HERO_TREES, al.heroTree, dl.heroTree),
          ground: pick(LAND_GROUNDS, al.ground, dl.ground),
          sky: pick(SKY_MODES, al.sky, dl.sky),
        },
      };
    } catch {
      return structuredClone(DEFAULTS);
    }
  }
  function persist() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    } catch {
      /* localStorage có thể không khả dụng */
    }
  }

  let params = $state<PanelParams>(loadSaved());

  // luôn giữ aspect (gửi backend) đồng bộ với size đã chọn
  $effect(() => {
    const a = aspectFromSize(params.size ?? '');
    if (params.aspect !== a) params.aspect = a;
  });

  // environment lọc theo renderType (10e); đổi loại làm environment không hợp lệ → reset hợp lệ
  const envOptions = $derived(environmentsFor(params.renderType));
  $effect(() => {
    if (!envOptions.includes(params.environment)) params.environment = envOptions[0];
  });

  const isInterior = $derived(params.renderType === 'interior');
  const isArchitecture = $derived(params.renderType === 'architecture');
  const isLandscape = $derived(params.renderType === 'landscape');
  // 'use-existing': giữ phòng sẵn → khoá tường/sàn/trần/backsplash + slider sáng tạo bối cảnh
  const lockRoom = $derived(isInterior && params.autoEnvironment.roomSource === 'use-existing');

  // ----- gợi ý Mô tả: nút "Điền mẫu tự động" xoay vòng 4 mẫu theo loại diễn hoạ -----
  const EXAMPLE_COUNT = 4;
  let exampleIdx = $state(0);
  // đổi loại diễn hoạ → bắt đầu lại từ mẫu đầu
  $effect(() => {
    void params.renderType;
    exampleIdx = 0;
  });
  function fillExample() {
    params.description = t(`airender.ex.${params.renderType}.${exampleIdx + 1}`);
    exampleIdx = (exampleIdx + 1) % EXAMPLE_COUNT;
  }

  let models = $state<AiModelDto[]>([]);
  let modelsLoaded = $state(false);
  let savedCount = $state(0);

  let busy = $state(false); // đang tạo (capture → poll)
  let statusKey = $state(''); // 'queued' | 'running'
  let result = $state<RenderDto | null>(null);
  let acting = $state(false); // đang lưu/xóa/đặt thumbnail

  const selectedModel = $derived(models.find((m) => m.id === params.aiModel) ?? null);
  // 03f: endpoint chỉ trả model của provider CÓ key → hasKey luôn true. Giữ check như lá chắn
  // phòng thủ (deploy lệch pha / endpoint cũ): không cho Tạo khi model thiếu key.
  const needKey = $derived(modelsLoaded && !!selectedModel && !selectedModel.hasKey);
  const canGenerate = $derived(
    !busy && modelsLoaded && models.length > 0 && !!selectedModel && selectedModel.hasKey,
  );
  const keepActive = $derived(params.materials.includes('keep'));
  // 03f: id namespaced provider:model → gom option theo provider cho dễ đọc
  const modelGroups = $derived.by(() => {
    const map = new Map<string, AiModelDto[]>();
    for (const m of models) {
      const arr = map.get(m.provider) ?? [];
      arr.push(m);
      map.set(m.provider, arr);
    }
    return [...map.entries()].map(([provider, items]) => ({ provider, items }));
  });
  function providerLabel(p: string): string {
    if (p === 'google') return 'Google';
    if (p === 'openai') return 'OpenAI';
    return p ? p.charAt(0).toUpperCase() + p.slice(1) : p;
  }

  // 04a: toggle chủ "Giữ nguyên mô hình 3D" đang BẬT.
  const locked = $derived(params.lockGeometry !== false);
  // 04a: provider hỗ trợ ảnh guide multi-image (v1: google + openai; alibaba chờ khảo sát §B4).
  const GUIDE_PROVIDERS = new Set(['google', 'openai']);
  const guideSupported = $derived(!!selectedModel && GUIDE_PROVIDERS.has(selectedModel.provider));
  // 04a A2b: khi khóa hình, accessory chỉ cho none/low (medium/high THÊM vật thể → mâu thuẫn "giữ hình").
  const accessoryOptions = $derived(
    locked ? ACCESSORY_LEVELS.filter((v) => v === 'none' || v === 'low') : ACCESSORY_LEVELS,
  );
  $effect(() => {
    if (locked && params.autoEnvironment.accessory !== 'none' && params.autoEnvironment.accessory !== 'low') {
      params.autoEnvironment.accessory = 'low'; // clamp khi bật khóa
    }
  });

  let cancelled = false;
  let pollTimer: ReturnType<typeof setTimeout> | undefined;

  onMount(async () => {
    try {
      const list = await getAiModels(token, authKey);
      models = list;
      // giữ aiModel đã lưu nếu vẫn còn trong registry; ngược lại chọn model đầu có key
      if (!list.some((m) => m.id === params.aiModel)) {
        const first = list.find((m) => m.hasKey) ?? list[0];
        if (first) params.aiModel = first.id;
      }
    } catch {
      /* để models rỗng → hiện thông báo không có model */
    }
    modelsLoaded = true;
  });

  onDestroy(() => {
    cancelled = true;
    clearTimeout(pollTimer);
  });

  // ----- toggle đa chọn (04b §1) -----
  function toggleLighting(v: Lighting) {
    const has = params.lighting.includes(v);
    params.lighting = has ? params.lighting.filter((x) => x !== v) : [...params.lighting, v];
    if (v === 'other' && has) params.lightingText = ''; // bỏ chọn "Khác" → clear text
  }
  function toggleColorTone(v: ColorTone) {
    params.colorTone = params.colorTone.includes(v)
      ? params.colorTone.filter((x) => x !== v)
      : [...params.colorTone, v];
  }
  /** 'keep' loại trừ với các vật liệu khác: chọn keep → chỉ keep; chọn khác → bỏ keep. */
  function toggleMaterial(v: Material) {
    if (v === 'keep') {
      params.materials = params.materials.includes('keep') ? [] : ['keep'];
      return;
    }
    const without = params.materials.filter((x) => x !== 'keep' && x !== v);
    params.materials = params.materials.includes(v) ? without : [...without, v];
  }

  function poll(id: number) {
    pollTimer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const dto = await getRender(token, authKey, id);
        if (cancelled) return;
        statusKey = dto.status;
        if (dto.status === 'done') {
          result = dto;
          busy = false;
        } else if (dto.status === 'failed') {
          busy = false;
          onToast(dto.error || t('airender.failed'));
        } else {
          poll(id); // queued/running → tiếp tục
        }
      } catch {
        if (cancelled) return;
        busy = false;
        onToast(t('airender.errGeneric'));
      }
    }, 2000);
  }

  async function generate() {
    if (!canGenerate) return;
    busy = true;
    result = null;
    statusKey = 'queued';
    // đường số backend không đổi (RenderRequest.creativity) → gửi creativity = contextCreativity (10e/04f §1)
    params.creativity = params.contextCreativity;
    persist(); // nhớ tham số lần render gần nhất
    try {
      const { w, h } = parseSize(params.size ?? '');
      const blob = await capture(w, h);
      // 04a: khóa hình + provider hỗ trợ → gửi kèm pass cấu trúc (line-pass) làm guide multi-image.
      // Provider không hỗ trợ → bỏ guide (tránh upload phí; backend cũng phòng thủ bỏ qua).
      const guide = locked && guideSupported ? await captureGuide(w, h) : undefined;
      const { id } = await createRender(token, authKey, blob, { ...params }, guide);
      poll(id);
    } catch (e) {
      busy = false;
      if (e instanceof ApiError) {
        const m = e.message;
        if (m.includes('render_cap')) onToast(t('airender.errCap'));
        else if (m.includes('quota_exceeded')) onToast(t('airender.errQuota'));
        else if (m.includes('no_credential')) onToast(t('airender.noKey'));
        else onToast(t('airender.errGeneric'));
      } else {
        onToast(t('airender.errGeneric'));
      }
    }
  }

  async function doSave() {
    if (!result || acting) return;
    acting = true;
    try {
      result = await saveRender(token, authKey, result.id);
      savedCount += 1;
      onToast(t('airender.toastSaved'));
      onSaved?.();
    } catch {
      onToast(t('airender.errGeneric'));
    }
    acting = false;
  }

  async function doDelete() {
    if (!result || acting) return;
    acting = true;
    try {
      const wasSaved = result.saved;
      await deleteRender(token, authKey, result.id);
      if (wasSaved) {
        savedCount = Math.max(0, savedCount - 1);
        onSaved?.();
      }
      result = null;
      onToast(t('airender.toastDeleted'));
    } catch {
      onToast(t('airender.errGeneric'));
    }
    acting = false;
  }

  async function doDownload() {
    if (!result?.imageUrl) return;
    try {
      const res = await fetch(result.imageUrl);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `render-${result.id}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch {
      onToast(t('airender.errGeneric'));
    }
  }

  async function doSetThumbnail() {
    if (!result?.imageUrl || acting) return;
    acting = true;
    try {
      const res = await fetch(result.imageUrl);
      const blob = await res.blob();
      await uploadThumbnail(token, authKey, blob);
      onToast(t('airender.toastThumbnail'));
    } catch {
      onToast(t('airender.errGeneric'));
    }
    acting = false;
  }

  function onBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget && !busy) onClose();
  }
</script>

<div class="ai-backdrop" role="presentation" onclick={onBackdrop}>
  <div class="ai-panel" role="dialog" aria-modal="true" aria-label={t('airender.title')}>
    <header class="ai-head">
      <h2><Icon name="sparkles" size={18} /> {t('airender.title')}</h2>
      <button class="x" aria-label={t('common.close')} onclick={onClose} disabled={busy}>
        <Icon name="close" size={18} />
      </button>
    </header>

    <div class="ai-body">
      <div class="ai-form">
        <!-- 04a: toggle chủ "Giữ nguyên mô hình 3D" (ràng buộc cứng + ảnh guide khi provider hỗ trợ) -->
        <div class="full ai-lock" class:on={locked}>
          <label class="ai-lock-row">
            <input type="checkbox" bind:checked={params.lockGeometry} />
            <span class="ai-lock-text">
              <strong>{t('airender.fieldLockGeometry')}</strong>
              <small>{t(locked ? 'airender.lockOnHint' : 'airender.lockOffHint')}</small>
            </span>
          </label>
        </div>

        <label>
          <span>{t('airender.fieldRenderType')}</span>
          <select bind:value={params.renderType}>
            {#each RENDER_TYPES as v (v)}
              <option value={v}>{t(aiOptLabelKey('renderType', v))}</option>
            {/each}
          </select>
          <small class="ai-hint">{t('airender.hint.renderType')}</small>
        </label>

        <label>
          <span>{t('airender.fieldViewAngle')}</span>
          <select bind:value={params.viewAngle}>
            {#each VIEW_ANGLES as v (v)}
              <option value={v}>{t(aiOptLabelKey('viewAngle', v))}</option>
            {/each}
          </select>
          <small class="ai-hint">{t('airender.viewAngleNote')}</small>
        </label>

        <label>
          <span>{t('airender.fieldEnvironment')}</span>
          <select bind:value={params.environment}>
            {#each envOptions as v (v)}
              <option value={v}>{t(aiOptLabelKey('environment', v))}</option>
            {/each}
          </select>
          <small class="ai-hint">{t('airender.hint.environment')}</small>
        </label>

        <label>
          <span>{t('airender.fieldSize')}</span>
          <select bind:value={params.size}>
            {#each SIZES as v (v)}
              <option value={v}>{t(aiOptLabelKey('size', v))}</option>
            {/each}
          </select>
          <small class="ai-hint">{t('airender.sizeNote')}</small>
        </label>

        {#if params.environment === 'other'}
          <label class="full">
            <span>{t('airender.fieldEnvironmentText')}</span>
            <input
              type="text"
              bind:value={params.environmentText}
              placeholder={t('airender.environmentTextPlaceholder')}
            />
          </label>
        {/if}

        <!-- Ánh sáng — đa chọn -->
        <div class="full chips-field">
          <span class="chips-label">{t('airender.fieldLighting')}</span>
          <div class="ai-chips">
            {#each LIGHTINGS as v (v)}
              <button
                type="button"
                class="chip"
                class:active={params.lighting.includes(v)}
                onclick={() => toggleLighting(v)}
              >
                {t(aiOptLabelKey('lighting', v))}
              </button>
            {/each}
          </div>
          <small class="ai-hint">{t('airender.hint.lighting')}</small>
        </div>

        {#if params.lighting.includes('other')}
          <label class="full">
            <span>{t('airender.fieldLightingText')}</span>
            <input
              type="text"
              bind:value={params.lightingText}
              placeholder={t('airender.lightingTextPlaceholder')}
            />
          </label>
        {/if}

        <!-- Tông màu — đa chọn -->
        <div class="full chips-field">
          <span class="chips-label">{t('airender.fieldColorTone')}</span>
          <div class="ai-chips">
            {#each COLOR_TONES as v (v)}
              <button
                type="button"
                class="chip"
                class:active={params.colorTone.includes(v)}
                onclick={() => toggleColorTone(v)}
              >
                {t(aiOptLabelKey('colorTone', v))}
              </button>
            {/each}
          </div>
          <small class="ai-hint">{t('airender.hint.colorTone')}</small>
        </div>

        <!-- Vật liệu — đa chọn ('keep' loại trừ phần còn lại) -->
        <div class="full chips-field">
          <span class="chips-label">{t('airender.fieldMaterials')}</span>
          <div class="ai-chips">
            {#each MATERIALS as v (v)}
              <button
                type="button"
                class="chip"
                class:active={params.materials.includes(v)}
                class:dim={keepActive && v !== 'keep'}
                onclick={() => toggleMaterial(v)}
              >
                {t(aiOptLabelKey('material', v))}
              </button>
            {/each}
          </div>
          <small class="ai-hint">{t('airender.hint.materials')}</small>
        </div>

        <!-- Tự tạo không gian phòng (10e) — chỉ Nội thất -->
        {#if isInterior}
          <details class="full ai-room" open>
            <summary>{t('airender.roomSection')}</summary>

            <div class="chips-field">
              <span class="chips-label">{t('airender.fieldRoomSource')}</span>
              <div class="ai-chips">
                {#each ROOM_SOURCES as v (v)}
                  <button
                    type="button"
                    class="chip"
                    class:active={params.autoEnvironment.roomSource === v}
                    onclick={() => (params.autoEnvironment.roomSource = v)}
                  >
                    {t(aiOptLabelKey('roomSource', v))}
                  </button>
                {/each}
              </div>
            </div>

            <div class="ai-room-grid">
              <label class:dim-field={lockRoom}>
                <span>{t('airender.fieldWall')}</span>
                <select bind:value={params.autoEnvironment.wall} disabled={lockRoom}>
                  {#each WALL_MODES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('wall', v))}</option>
                  {/each}
                </select>
              </label>
              <label class:dim-field={lockRoom}>
                <span>{t('airender.fieldFloor')}</span>
                <select bind:value={params.autoEnvironment.floor} disabled={lockRoom}>
                  {#each FLOOR_MODES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('floor', v))}</option>
                  {/each}
                </select>
              </label>
              <label class:dim-field={lockRoom}>
                <span>{t('airender.fieldCeiling')}</span>
                <select bind:value={params.autoEnvironment.ceiling} disabled={lockRoom}>
                  {#each CEILING_MODES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('ceiling', v))}</option>
                  {/each}
                </select>
              </label>
              <label class:dim-field={lockRoom}>
                <span>{t('airender.fieldBacksplash')}</span>
                <select bind:value={params.autoEnvironment.backsplash} disabled={lockRoom}>
                  {#each BACKSPLASH_MODES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('backsplash', v))}</option>
                  {/each}
                </select>
              </label>
              {#if !locked}
                <!-- 04a: ẩn picker "Đảo bếp" (thêm khối) khi khóa hình — tránh no-op thầm lặng -->
                <label>
                  <span>{t('airender.fieldIsland')}</span>
                  <select bind:value={params.autoEnvironment.island}>
                    {#each ISLAND_MODES as v (v)}
                      <option value={v}>{t(aiOptLabelKey('island', v))}</option>
                    {/each}
                  </select>
                </label>
              {/if}
              <label>
                <span>{t('airender.fieldAccessory')}</span>
                <select bind:value={params.autoEnvironment.accessory}>
                  {#each accessoryOptions as v (v)}
                    <option value={v}>{t(aiOptLabelKey('accessory', v))}</option>
                  {/each}
                </select>
              </label>
            </div>
            <p class="ai-note">{t('airender.roomNote')}</p>
          </details>
        {/if}

        <!-- Bối cảnh Kiến trúc (10f/04g) — chỉ architecture -->
        {#if isArchitecture}
          <details class="full ai-room" open>
            <summary>{t('airender.archSection')}</summary>
            <div class="ai-room-grid">
              <label>
                <span>{t('airender.fieldScene')}</span>
                <select bind:value={params.autoArchitecture.sceneType}>
                  {#each ARCH_SCENES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('archScene', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldArchSurrounding')}</span>
                <select bind:value={params.autoArchitecture.surroundingBuildings}>
                  {#each ARCH_SURROUNDINGS as v (v)}
                    <option value={v}>{t(aiOptLabelKey('archSurrounding', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldArchActivity')}</span>
                <select bind:value={params.autoArchitecture.activity}>
                  {#each ARCH_ACTIVITY as v (v)}
                    <option value={v}>{t(aiOptLabelKey('archActivity', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldArchGround')}</span>
                <select bind:value={params.autoArchitecture.ground}>
                  {#each ARCH_GROUNDS as v (v)}
                    <option value={v}>{t(aiOptLabelKey('archGround', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldSky')}</span>
                <select bind:value={params.autoArchitecture.sky}>
                  {#each SKY_MODES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('sky', v))}</option>
                  {/each}
                </select>
              </label>
            </div>
            <p class="ai-note">{t('airender.sceneVsEnvNote')}</p>
          </details>
        {/if}

        <!-- Bối cảnh Cảnh quan (10f/04g) — chỉ landscape -->
        {#if isLandscape}
          <details class="full ai-room" open>
            <summary>{t('airender.landSection')}</summary>
            <div class="ai-room-grid">
              <label>
                <span>{t('airender.fieldScene')}</span>
                <select bind:value={params.autoLandscape.sceneType}>
                  {#each LAND_SCENES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('landScene', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldLandTerrain')}</span>
                <select bind:value={params.autoLandscape.terrain}>
                  {#each LAND_TERRAINS as v (v)}
                    <option value={v}>{t(aiOptLabelKey('landTerrain', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldLandWater')}</span>
                <select bind:value={params.autoLandscape.water}>
                  {#each LAND_WATER as v (v)}
                    <option value={v}>{t(aiOptLabelKey('landWater', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldLandHeroTree')}</span>
                <select bind:value={params.autoLandscape.heroTree}>
                  {#each LAND_HERO_TREES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('landHeroTree', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldLandGround')}</span>
                <select bind:value={params.autoLandscape.ground}>
                  {#each LAND_GROUNDS as v (v)}
                    <option value={v}>{t(aiOptLabelKey('landGround', v))}</option>
                  {/each}
                </select>
              </label>
              <label>
                <span>{t('airender.fieldSky')}</span>
                <select bind:value={params.autoLandscape.sky}>
                  {#each SKY_MODES as v (v)}
                    <option value={v}>{t(aiOptLabelKey('sky', v))}</option>
                  {/each}
                </select>
              </label>
            </div>
            <p class="ai-note">{t('airender.sceneVsEnvNote')}</p>
          </details>
        {/if}

        <label class="full">
          <span>{t('airender.fieldModel')}</span>
          <select bind:value={params.aiModel} disabled={models.length === 0}>
            {#if models.length === 0}
              <option value="">{t('airender.noModels')}</option>
            {:else if modelGroups.length === 1}
              {#each modelGroups[0].items as m (m.id)}
                <option value={m.id}>{formatAiModelLabel(m, t)}</option>
              {/each}
            {:else}
              {#each modelGroups as g (g.provider)}
                <optgroup label={providerLabel(g.provider)}>
                  {#each g.items as m (m.id)}
                    <option value={m.id}>{formatAiModelLabel(m, t)}</option>
                  {/each}
                </optgroup>
              {/each}
            {/if}
          </select>
          <small class="ai-hint">{t('airender.hint.model')}</small>
        </label>

        <label class="full">
          <span class="field-head">
            <span>{t('airender.fieldDescription')}</span>
            <button type="button" class="example-btn" onclick={fillExample}>
              <Icon name="sparkles" size={13} />
              {t('airender.fillExample')}
            </button>
          </span>
          <textarea
            rows="3"
            bind:value={params.description}
            placeholder={t('airender.descPlaceholder')}
          ></textarea>
          <small class="ai-hint">{t(`airender.hint.desc.${params.renderType}`)}</small>
        </label>

        <label class="full">
          <span>{t('airender.fieldNegative')}</span>
          <textarea
            rows="2"
            bind:value={params.negativePrompt}
            placeholder={t('airender.negativePlaceholder')}
          ></textarea>
          <small class="ai-hint">{t('airender.hint.negative')}</small>
        </label>

        {#if !locked}
          <!-- 04a: ẩn slider "Giữ thiết kế tủ" khi khóa hình — lock (cứng) đã thắng strength (mềm) -->
          <label class="full">
            <span>{t('airender.fieldPreserveFurniture')} — {params.preserveFurnitureStrength}</span>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              bind:value={params.preserveFurnitureStrength}
            />
          </label>
        {/if}
        <label class="full" class:dim-field={lockRoom}>
          <span>{t('airender.fieldContextCreativity')} — {params.contextCreativity}</span>
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            bind:value={params.contextCreativity}
            disabled={lockRoom}
          />
        </label>
        <p class="ai-note">{t('airender.creativityNote')}</p>
      </div>

      {#if needKey}
        <p class="ai-warn">
          {t('airender.noKey')}
          <a href="/app/settings" target="_blank" rel="noopener">{t('airender.openConsole')}</a>
        </p>
      {/if}

      <!-- kết quả / tiến trình -->
      <div class="ai-result">
        {#if busy}
          <div class="ai-progress">
            <div class="spinner" aria-hidden="true"></div>
            <p>{statusKey === 'running' ? t('airender.statusRunning') : t('airender.statusQueued')}</p>
          </div>
        {:else if result?.imageUrl}
          <img src={result.imageUrl} alt={t('airender.title')} />
          <div class="ai-actions">
            {#if !result.saved}
              <button class="primary" disabled={acting} onclick={doSave}>
                <Icon name="save" size={16} /><span>{t('airender.save')}</span>
              </button>
            {:else}
              <span class="saved-badge"><Icon name="check" size={14} /> {t('airender.savedBadge')}</span>
            {/if}
            <button disabled={acting} onclick={doDownload}>
              <Icon name="download" size={16} /><span>{t('airender.download')}</span>
            </button>
            <button disabled={acting} onclick={doSetThumbnail}>
              <Icon name="image" size={16} /><span>{t('airender.setThumbnail')}</span>
            </button>
            <button class="danger" disabled={acting} onclick={doDelete}>
              <Icon name="trash" size={16} /><span>{t('airender.delete')}</span>
            </button>
          </div>
        {:else}
          <p class="ai-empty">{t('airender.resultHint')}</p>
        {/if}
      </div>
    </div>

    <footer class="ai-foot">
      {#if savedCount > 0}
        <span class="ai-count">{t('airender.savedCount', { n: savedCount })}</span>
      {/if}
      <button class="generate" disabled={!canGenerate} onclick={generate}>
        <Icon name="sparkles" size={18} />
        {busy ? t('airender.generating') : t('airender.generate')}
      </button>
    </footer>
  </div>
</div>

<style>
  .ai-backdrop {
    position: fixed;
    inset: 0;
    z-index: var(--z-modal);
    display: grid;
    place-items: center;
    padding: var(--space-md);
    background: rgba(11, 31, 58, 0.42);
    backdrop-filter: blur(2px);
  }
  .ai-panel {
    display: flex;
    flex-direction: column;
    width: min(560px, 100%);
    max-height: 92vh;
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-panel);
    box-shadow: var(--shadow-panel);
    overflow: hidden;
  }
  .ai-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-md);
    border-bottom: 1px solid var(--color-border);
  }
  .ai-head h2 {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: var(--font-size-body);
    font-weight: 700;
    color: var(--color-navy);
  }
  .ai-head .x {
    border: none;
    background: transparent;
    color: var(--color-text-secondary);
    cursor: pointer;
    padding: 4px;
    border-radius: var(--radius-control);
  }
  .ai-head .x:hover:not(:disabled) {
    background: var(--color-canvas-bottom);
    color: var(--color-primary);
  }
  .ai-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: var(--space-md);
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
  }
  .ai-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-sm);
  }
  .ai-form label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: var(--font-size-caption);
    font-weight: 600;
    color: var(--color-text-secondary);
    min-width: 0;
  }
  .ai-form label.full,
  .ai-form .full {
    grid-column: 1 / -1;
  }
  .ai-form select,
  .ai-form textarea,
  .ai-form input[type='text'] {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    padding: 7px 8px;
    font: inherit;
    background: var(--color-surface);
    color: var(--color-text-primary);
  }
  .ai-form textarea {
    resize: vertical;
  }
  .ai-form input[type='range'] {
    width: 100%;
    accent-color: var(--color-primary);
    cursor: pointer;
  }
  .chips-field {
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
  .chips-label {
    font-size: var(--font-size-caption);
    font-weight: 600;
    color: var(--color-text-secondary);
  }
  .ai-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .chip {
    padding: 5px 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-pill);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
    transition:
      border-color 0.12s,
      background 0.12s,
      opacity 0.12s;
  }
  .chip:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .chip.active {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  .chip.dim {
    opacity: 0.4;
  }
  .ai-note {
    grid-column: 1 / -1;
    margin: 0;
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
    line-height: 1.4;
  }
  /* 04a: toggle chủ "Giữ nguyên mô hình 3D" — nổi bật ở đầu form */
  .ai-lock {
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    padding: 10px 12px;
    background: var(--color-surface);
  }
  .ai-lock.on {
    border-color: var(--color-primary);
    background: color-mix(in srgb, var(--color-primary) 8%, var(--color-surface));
  }
  .ai-lock-row {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    cursor: pointer;
  }
  .ai-lock-row input {
    margin-top: 2px;
    width: 18px;
    height: 18px;
    accent-color: var(--color-primary);
    flex-shrink: 0;
    cursor: pointer;
  }
  .ai-lock-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .ai-lock-text strong {
    font-size: var(--font-size-body);
    font-weight: 700;
  }
  .ai-lock-text small {
    font-size: 11px;
    color: var(--color-text-secondary);
    line-height: 1.4;
  }
  /* hint giải thích dưới từng ô (nhẹ, cỡ nhỏ) */
  .ai-hint {
    display: block;
    margin-top: 2px;
    font-size: 11px;
    font-weight: 400;
    line-height: 1.35;
    color: var(--color-text-secondary);
    opacity: 0.85;
  }
  /* hàng tiêu đề ô Mô tả: nhãn + nút "Điền mẫu tự động" */
  .field-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }
  .example-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 9px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-pill);
    background: var(--color-surface);
    color: var(--color-primary);
    font: inherit;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
  }
  .example-btn:hover {
    border-color: var(--color-primary);
    background: var(--color-canvas-bottom);
  }
  /* "Tự tạo không gian phòng" (10e) */
  .ai-room {
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    padding: var(--space-sm);
    display: flex;
    flex-direction: column;
    gap: var(--space-sm);
  }
  .ai-room summary {
    cursor: pointer;
    user-select: none;
    font-size: var(--font-size-caption);
    font-weight: 700;
    color: var(--color-navy);
  }
  .ai-room-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-sm);
  }
  /* trường bị khoá khi roomSource='use-existing' (làm mờ; <select>/<input> disabled riêng) */
  .dim-field {
    opacity: 0.45;
  }
  .ai-warn {
    margin: 0;
    padding: 8px 10px;
    border-radius: var(--radius-control);
    background: #fff4e0;
    color: var(--color-warning);
    font-size: var(--font-size-caption);
    line-height: 1.4;
  }
  .ai-warn a {
    color: var(--color-primary);
    font-weight: 700;
  }
  .ai-result {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-sm);
    min-height: 120px;
    /* KHÔNG co dưới chiều cao nội dung: nếu không, ảnh kết quả (cao hơn min-height) bị
       căn-giữa tràn LÊN che slider/ghi chú phía trên (flex item shrink trong .ai-body cuộn). */
    flex-shrink: 0;
    justify-content: center;
    overflow: hidden;
    border: 1px dashed var(--color-border);
    border-radius: var(--radius-control);
    padding: var(--space-sm);
  }
  .ai-result img {
    max-width: 100%;
    max-height: 42vh;
    border-radius: var(--radius-control);
    display: block;
  }
  .ai-empty {
    margin: 0;
    color: var(--color-text-secondary);
    font-size: var(--font-size-caption);
    text-align: center;
  }
  .ai-progress {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-sm);
    color: var(--color-text-secondary);
  }
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid var(--color-border);
    border-top-color: var(--color-primary);
    border-radius: 50%;
    animation: ai-spin 0.8s linear infinite;
  }
  @keyframes ai-spin {
    to {
      transform: rotate(360deg);
    }
  }
  .ai-actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
  }
  .ai-actions button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 34px;
    padding: 0 10px;
    border: 1px solid var(--color-border);
    border-radius: var(--radius-control);
    background: var(--color-surface);
    color: var(--color-text-primary);
    font: inherit;
    font-size: var(--font-size-caption);
    font-weight: 600;
    cursor: pointer;
  }
  .ai-actions button:hover:not(:disabled) {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .ai-actions button.primary {
    background: var(--color-primary);
    border-color: var(--color-primary);
    color: #fff;
  }
  .ai-actions button.danger:hover:not(:disabled) {
    border-color: var(--color-error);
    color: var(--color-error);
  }
  .ai-actions button:disabled {
    opacity: 0.5;
    cursor: default;
  }
  .saved-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: var(--font-size-caption);
    font-weight: 700;
    color: var(--color-primary);
  }
  .ai-foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--space-sm);
    padding: var(--space-md);
    border-top: 1px solid var(--color-border);
  }
  .ai-count {
    margin-right: auto;
    font-size: var(--font-size-caption);
    color: var(--color-text-secondary);
  }
  .generate {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: var(--size-touch-min);
    padding: 0 var(--space-lg);
    border: none;
    border-radius: var(--radius-control);
    background: var(--color-primary);
    color: #fff;
    font: inherit;
    font-weight: 700;
    cursor: pointer;
  }
  .generate:hover:not(:disabled) {
    background: var(--color-primary-accent);
  }
  .generate:disabled {
    background: var(--color-border);
    color: var(--color-text-secondary);
    cursor: default;
  }

  @media (max-width: 767px) {
    .ai-form {
      grid-template-columns: 1fr;
    }
    .ai-room-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
